import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Share2, Trophy, Loader2, AlertCircle, ChevronDown, ChevronUp, Calendar, MapPin, Download } from 'lucide-react';
import { getRoundName } from '../utils/tournamentRoundName';
import { buildPlayerById, resolveSideNames, resolveEntityName, resolveMatchSide } from '../utils/tournamentPlayers';
import { forAgainstLabels } from '../utils/tournamentLabels';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 30000;

const FORMAT_LABELS = {
  knockout: 'Knockout',
  double_elimination: 'Double Elimination',
  round_robin: 'Round Robin',
  group_knockout: 'Groups + Knockout',
  americano: 'Americano',
  mexicano: 'Mexicano',
  mixed_americano: 'Mixed Americano',
  king_of_court: 'King of the Court',
};
const INDIVIDUAL = new Set(['americano', 'mexicano', 'mixed_americano']);


// ─────────────────────────────────────────────────────────────
// Polling hook
// ─────────────────────────────────────────────────────────────
const useTournamentPolling = (slug) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const timer = useRef(null);

  const fetchOnce = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/tournaments/public/${slug}`);
      if (r.status === 404) {
        setError('Tournament not found');
        setData(null);
        return;
      }
      if (!r.ok) throw new Error('Failed to load tournament');
      const j = await r.json();
      setData(j);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchOnce();
    timer.current = setInterval(fetchOnce, POLL_INTERVAL_MS);
    return () => clearInterval(timer.current);
  }, [fetchOnce]);

  return { data, loading, error, refetch: fetchOnce };
};


// ─────────────────────────────────────────────────────────────
// Top-level
// ─────────────────────────────────────────────────────────────
const TournamentDetailPublic = () => {
  const { slug } = useParams();
  const nav = useNavigate();
  const { data, loading, error } = useTournamentPolling(slug);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;
  }
  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center">
        <AlertCircle className="h-10 w-10 text-red-500 mb-2" />
        <p className="text-slate-800 font-medium">{error}</p>
        <button onClick={() => nav('/')} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm">Home</button>
      </div>
    );
  }

  const tnm = data;
  const teamById = Object.fromEntries((tnm.teams || []).map(t => [t.id, t]));

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="tournament-public">
      <TopBar tnm={tnm} />
      <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
        <TournamentHeader tnm={tnm} teamById={teamById} />
        {tnm.status === 'setup' && <SetupBanner />}
        {tnm.status === 'cancelled' && <CancelledBanner />}
        {tnm.status !== 'setup' && <FormatView tnm={tnm} teamById={teamById} />}
        {tnm.status !== 'setup' && tnm.sport === 'football' && <PublicTopScorers tnm={tnm} />}
      </div>
      <FooterRefresh />
    </div>
  );
};
export default TournamentDetailPublic;


// ─────────────────────────────────────────────────────────────
// Top bar with WhatsApp + native share
// ─────────────────────────────────────────────────────────────
const TopBar = ({ tnm }) => {
  const nav = useNavigate();
  const url = typeof window !== 'undefined' ? window.location.href : '';
  const pdfUrl = `${API_BASE}/api/tournaments/public/${tnm.slug}/fixtures.pdf?t=${Date.now()}`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(
    `Follow ${tnm.title} live: ${url}`,
  )}`;
  const handleNative = () => {
    if (navigator.share) navigator.share({ title: tnm.title, url }).catch(() => {});
    else if (navigator.clipboard?.writeText) navigator.clipboard.writeText(url);
  };
  return (
    <header className="bg-white border-b sticky top-0 z-20">
      <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
        <button onClick={() => nav(-1)} className="p-2 -ml-2 hover:bg-gray-100 rounded-lg" data-testid="tournament-public-back">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="flex-1 text-base font-bold truncate">{tnm.title}</h1>
        {tnm.status !== 'setup' && (
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
             className="p-2 hover:bg-blue-50 rounded-lg text-blue-600"
             title="Download fixtures PDF"
             aria-label="Download fixtures PDF"
             data-testid="tournament-public-download-pdf">
            <Download className="h-4 w-4" />
          </a>
        )}
        <a href={whatsappUrl} target="_blank" rel="noopener noreferrer"
           className="p-2 hover:bg-emerald-50 rounded-lg text-[#25D366]"
           title="Share on WhatsApp"
           aria-label="Share on WhatsApp"
           data-testid="tournament-public-share-whatsapp">
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
            <path d="M19.05 4.91A9.82 9.82 0 0012.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 004.78 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7zM12.04 20.15h-.01a8.21 8.21 0 01-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.18 8.18 0 01-1.26-4.38c0-4.54 3.7-8.23 8.25-8.23 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 012.41 5.82c.01 4.54-3.69 8.23-8.23 8.23zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.12-.16.25-.64.81-.78.97-.14.17-.29.19-.54.06-.25-.12-1.04-.38-1.98-1.22-.73-.65-1.23-1.46-1.37-1.71-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.23.25-.86.84-.86 2.05 0 1.21.89 2.38 1.01 2.54.12.17 1.75 2.68 4.24 3.76.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.47-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.06-.1-.22-.16-.47-.28z"/>
          </svg>
        </a>
        <button onClick={handleNative} className="p-2 -mr-2 hover:bg-gray-100 rounded-lg" title="Share" data-testid="tournament-public-share">
          <Share2 className="h-4 w-4" />
        </button>
      </div>
    </header>
  );
};

// ─────────────────────────────────────────────────────────────
// Header card + champion banner
// ─────────────────────────────────────────────────────────────
const TournamentHeader = ({ tnm, teamById }) => {
  const champion = tnm.status === 'completed' && tnm.champion_team_id
    ? (teamById[tnm.champion_team_id] || { team_name: tnm.champion_team_id })
    : null;
  return (
    <>
      {champion && (
        <div className="bg-gradient-to-br from-amber-100 via-yellow-50 to-orange-100 border border-amber-300 rounded-2xl p-6 text-center" data-testid="tournament-champion-banner">
          <Trophy className="h-12 w-12 text-amber-600 mx-auto mb-2" />
          <div className="text-xs uppercase font-semibold text-amber-800 tracking-wide">Champion</div>
          <div className="text-2xl font-bold text-slate-900 mt-1" data-testid="tournament-champion-name">{champion.team_name}</div>
        </div>
      )}
      <div className="bg-white rounded-2xl border p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="text-xl font-bold text-slate-900">{tnm.title}</div>
            <div className="text-sm text-slate-500 mt-1 capitalize flex flex-wrap gap-x-3 gap-y-1">
              <span>{tnm.sport}</span>
              <span>· {FORMAT_LABELS[tnm.format] || tnm.format}</span>
              {tnm.venue_name && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{tnm.venue_name}</span>}
              {tnm.match_date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{tnm.match_date}{tnm.start_time ? ` · ${tnm.start_time}` : ''}</span>}
            </div>
          </div>
          <StatusPill status={tnm.status} />
        </div>
        {tnm.status === 'active' && tnm.current_round > 0 && (
          <div className="mt-3 text-sm font-medium text-blue-700">
            {getRoundName(tnm, tnm.current_round)} of {tnm.total_rounds || '?'} · Live
          </div>
        )}
      </div>
    </>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    setup: ['bg-slate-100 text-slate-700', 'Not started'],
    registration: ['bg-amber-100 text-amber-700', 'Registration'],
    active: ['bg-blue-100 text-blue-700', 'Live'],
    completed: ['bg-emerald-100 text-emerald-700', 'Completed'],
    cancelled: ['bg-red-100 text-red-700', 'Cancelled'],
  };
  const [cls, label] = map[status] || ['bg-slate-100 text-slate-700', status];
  return <span className={`text-[10px] font-semibold uppercase px-2 py-1 rounded-full shrink-0 ${cls}`} data-testid={`tournament-status-${status}`}>{label}</span>;
};

const SetupBanner = () => (
  <div className="bg-white border-2 border-dashed border-slate-300 rounded-2xl p-8 text-center" data-testid="tournament-setup-banner">
    <Calendar className="h-10 w-10 text-slate-400 mx-auto mb-2" />
    <div className="text-lg font-semibold text-slate-700">Tournament not started yet</div>
    <div className="text-sm text-slate-500 mt-1">Fixtures will appear here once the organiser starts the tournament.</div>
  </div>
);
const CancelledBanner = () => (
  <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
    <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
    <div className="text-base font-semibold text-red-900">Tournament cancelled</div>
  </div>
);


// ─────────────────────────────────────────────────────────────
// Format dispatcher
// ─────────────────────────────────────────────────────────────
const FormatView = ({ tnm, teamById }) => {
  const fmt = tnm.format;
  if (fmt === 'knockout' || fmt === 'double_elimination') return <BracketView tnm={tnm} teamById={teamById} />;
  if (fmt === 'round_robin') return <RoundRobinView tnm={tnm} teamById={teamById} />;
  if (fmt === 'group_knockout') return <GroupKnockoutView tnm={tnm} teamById={teamById} />;
  if (INDIVIDUAL.has(fmt)) return <AmericanoView tnm={tnm} teamById={teamById} />;
  if (fmt === 'king_of_court') return <KingView tnm={tnm} teamById={teamById} />;
  return null;
};


// ─────────────────────────────────────────────────────────────
// Bracket (knockout / double-elim) — horizontally scrollable columns
// ─────────────────────────────────────────────────────────────
const BracketView = ({ tnm, teamById }) => {
  const matches = tnm.matches || [];
  // Group by bracket (winners/losers/grand_final/reset/main), then by round
  const bracketGroups = useMemo(() => {
    const out = { main: {}, winners: {}, losers: {}, grand_final: {}, reset: {} };
    for (const m of matches) {
      const br = m.bracket || 'main';
      const key = out[br] !== undefined ? br : 'main';
      (out[key][m.round] = out[key][m.round] || []).push(m);
    }
    for (const br of Object.keys(out)) {
      for (const r of Object.keys(out[br])) {
        out[br][r].sort((a, b) => a.match_number - b.match_number);
      }
    }
    return out;
  }, [matches]);

  const renderBracket = (label, group, testid) => {
    const rounds = Object.keys(group).map(Number).sort((a, b) => a - b);
    if (rounds.length === 0) return null;
    return (
      <div className="bg-white rounded-2xl border p-4" data-testid={testid}>
        {label && <div className="text-sm font-semibold text-slate-700 mb-3">{label}</div>}
        <div className="overflow-x-auto -mx-4 px-4 pb-2">
          <div className="flex gap-6 min-w-max">
            {rounds.map(r => (
              <div key={r} className="w-56 shrink-0">
                <div className="text-xs uppercase font-semibold text-slate-500 mb-2">
                  {getRoundName(tnm, r, group[r][0])}
                </div>
                <div className="space-y-3">
                  {group[r].map(m => <BracketMatch key={m.id} match={m} teamById={teamById} currentRound={tnm.current_round} />)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  if (tnm.format === 'double_elimination') {
    return (
      <div className="space-y-4">
        {renderBracket('Winners Bracket', bracketGroups.winners, 'bracket-winners')}
        {renderBracket('Losers Bracket', bracketGroups.losers, 'bracket-losers')}
        {renderBracket('Grand Final', bracketGroups.grand_final, 'bracket-grand-final')}
        {Object.keys(bracketGroups.reset).length > 0 && renderBracket('Grand Final Reset', bracketGroups.reset, 'bracket-reset')}
      </div>
    );
  }
  return renderBracket(null, bracketGroups.main, 'bracket-main');
};

const BracketMatch = ({ match, teamById, currentRound }) => {
  const t1 = teamById[match.team1_id];
  const t2 = teamById[match.team2_id];
  const t1Name = t1?.team_name || (match.team1_id ? '—' : 'TBD');
  const t2Name = t2?.team_name || (match.team2_id ? '—' : 'TBD');
  const completed = match.status === 'completed' || match.status === 'bye';
  const current = match.round === currentRound && match.status !== 'completed' && match.status !== 'bye';
  const w1 = completed && match.winner_id === match.team1_id;
  const w2 = completed && match.winner_id === match.team2_id;
  // Walkover: completed/bye with no score recorded (BYE auto-advance or opponent no-show)
  const isWalkover = completed && match.score_team1 == null && match.score_team2 == null;
  // Show tiebreaker (penalty) score per side when decided by shootout
  const tbUsed = !!match.tiebreaker_used
    && match.tiebreaker_score_team1 != null
    && match.tiebreaker_score_team2 != null;
  return (
    <div className={`rounded-lg border overflow-hidden ${current ? 'border-blue-400 ring-2 ring-blue-100' : completed ? 'border-slate-200 opacity-90' : 'border-slate-200'}`}
         data-testid={`bracket-match-${match.id}`}>
      <BracketTeamRow name={t1Name} score={match.score_team1} winner={w1} dim={completed && !w1}
        tbScore={tbUsed ? match.tiebreaker_score_team1 : null}
        walkover={isWalkover} walkoverWinner={w1} />
      <div className="h-px bg-slate-200" />
      <BracketTeamRow name={t2Name} score={match.score_team2} winner={w2} dim={completed && !w2}
        tbScore={tbUsed ? match.tiebreaker_score_team2 : null}
        walkover={isWalkover} walkoverWinner={w2} />
    </div>
  );
};
const BracketTeamRow = ({ name, score, winner, dim, tbScore, walkover, walkoverWinner }) => (
  <div className={`flex items-center justify-between px-3 py-2 text-sm ${winner ? 'bg-emerald-50 font-bold text-emerald-900' : dim ? 'text-slate-400' : 'text-slate-800'}`}>
    <span className="truncate">{name}</span>
    <span className="font-mono font-semibold ml-2 shrink-0 flex items-baseline gap-1">
      {walkover ? (
        <span
          className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${walkoverWinner ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}
          data-testid="bracket-walkover-badge"
        >
          {walkoverWinner ? 'Walkover' : '—'}
        </span>
      ) : (
        <span>{score ?? '—'}</span>
      )}
      {tbScore != null && (
        <span className={`text-[10px] font-normal ${winner ? 'text-emerald-700' : 'text-slate-500'}`} data-testid="bracket-tb-score">
          ({tbScore})
        </span>
      )}
    </span>
  </div>
);


// ─────────────────────────────────────────────────────────────
// Round Robin
// ─────────────────────────────────────────────────────────────
const RoundRobinView = ({ tnm, teamById }) => (
  <>
    <StandingsTable tnm={tnm} standings={tnm.standings || []} teamById={teamById} />
    <MatchesByRound tnm={tnm} matches={tnm.matches || []} teamById={teamById} />
  </>
);

const StandingsTable = ({ tnm, standings, teamById, group = null, testid = 'standings-table' }) => {
  const isFootball = tnm.sport === 'football';
  const labels = forAgainstLabels(tnm.sport);
  // Show the diff column for football (goal difference) AND racquet sports
  // (points/games difference). Only cricket falls through to a lean view.
  const showDiff = isFootball || labels.diff === 'PD';
  // Sort by the same rule as the admin panel / backend ranking:
  // points DESC, goal-difference DESC, goals-for DESC, team_id ASC.
  const sorted = [...(standings || [])].sort((a, b) =>
    (b.points || 0) - (a.points || 0)
    || (b.goal_difference || 0) - (a.goal_difference || 0)
    || (b.goals_for || 0) - (a.goals_for || 0)
    || String(a.team_id || '').localeCompare(String(b.team_id || ''))
  );
  return (
    <div className="bg-white rounded-2xl border p-4 overflow-x-auto" data-testid={testid}>
      {group && <div className="text-sm font-semibold text-slate-700 mb-2">Group {group}</div>}
      {!group && <div className="text-sm font-semibold text-slate-700 mb-3">Standings</div>}
      <table className="w-full text-sm">
        <thead className="text-xs uppercase text-slate-500 border-b">
          <tr>
            <th className="text-left py-2">#</th>
            <th className="text-left">Team</th>
            <th className="text-right px-2">P</th><th className="text-right px-2">W</th>
            <th className="text-right px-2">D</th><th className="text-right px-2">L</th>
            {showDiff && <th className="text-right px-2">{labels.diff}</th>}
            <th className="text-right px-2">Pts</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((s, i) => (
            <tr key={s.id} className="border-b last:border-0">
              <td className="py-2 font-mono text-slate-500">{i + 1}</td>
              <td className="font-medium text-slate-900">{teamById[s.team_id]?.team_name || s.team_id}</td>
              <td className="text-right px-2">{s.played || 0}</td>
              <td className="text-right px-2">{s.won || 0}</td>
              <td className="text-right px-2">{s.drawn || 0}</td>
              <td className="text-right px-2">{s.lost || 0}</td>
              {showDiff && <td className="text-right px-2">{s.goal_difference || 0}</td>}
              <td className="text-right px-2 font-bold text-slate-900">{s.points || 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const MatchesByRound = ({ tnm, matches, teamById, playerById = {}, collapseCompleted = false }) => {
  const grouped = useMemo(() => {
    const out = {};
    for (const m of matches) (out[m.round] = out[m.round] || []).push(m);
    for (const r of Object.keys(out)) out[r].sort((a, b) => (a.court || 0) - (b.court || 0));
    return out;
  }, [matches]);
  const cur = tnm.current_round || 0;
  const rounds = Object.keys(grouped).map(Number).sort((a, b) => a - b);
  return (
    <div className="space-y-3" data-testid="matches-by-round">
      {rounds.map(r => (
        <RoundCard key={r} round={r} matches={grouped[r]} teamById={teamById} playerById={playerById} tnm={tnm}
          isCurrent={r === cur && tnm.status === 'active'} collapsedDefault={collapseCompleted && r < cur} />
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Group-wise fixtures for the public follower page. One
// collapsible section per group (A/B/C…), rounds shown as
// sub-headings inside. Falls back to the flat `MatchesByRound`
// view when no match has a `group` label (legacy tournaments).
// ─────────────────────────────────────────────────────────────
const MatchesByGroup = ({ tnm, matches, teamById, playerById = {} }) => {
  const byGroup = useMemo(() => {
    const out = {};
    for (const m of matches) {
      const g = m.group || '_';
      (out[g] = out[g] || []).push(m);
    }
    for (const g of Object.keys(out)) {
      out[g].sort((a, b) => (a.round || 0) - (b.round || 0)
        || (a.court || 0) - (b.court || 0)
        || (a.match_number || 0) - (b.match_number || 0));
    }
    return out;
  }, [matches]);
  const groups = Object.keys(byGroup).filter(g => g !== '_').sort();
  const ungrouped = byGroup['_'] || [];
  // Legacy fallback: nothing has a group tag → keep the old round view
  if (groups.length === 0 && ungrouped.length > 0) {
    return <MatchesByRound tnm={tnm} matches={ungrouped} teamById={teamById}
                           playerById={playerById} collapseCompleted />;
  }
  return (
    <div className="space-y-3" data-testid="matches-by-group">
      {groups.map(g => (
        <GroupFixturesCard key={g} group={g} matches={byGroup[g]}
                           tnm={tnm} teamById={teamById} playerById={playerById} />
      ))}
      {ungrouped.length > 0 && (
        <GroupFixturesCard group="Other" matches={ungrouped}
                           tnm={tnm} teamById={teamById} playerById={playerById} />
      )}
    </div>
  );
};

const GroupFixturesCard = ({ group, matches, tnm, teamById, playerById = {} }) => {
  const [collapsed, setCollapsed] = useState(false);
  const done = matches.filter(m => m.status === 'completed' || m.status === 'bye').length;
  const total = matches.length;
  const allDone = done === total;
  // Bucket by round within the group so tap-open shows rounds cleanly.
  const byRound = useMemo(() => {
    const out = {};
    for (const m of matches) (out[m.round || 0] = out[m.round || 0] || []).push(m);
    for (const r of Object.keys(out)) {
      out[r].sort((a, b) => (a.court || 0) - (b.court || 0)
        || (a.match_number || 0) - (b.match_number || 0));
    }
    return out;
  }, [matches]);
  const rounds = Object.keys(byRound).map(Number).sort((a, b) => a - b);
  return (
    <div className="bg-white rounded-2xl border" data-testid={`group-fixtures-${group}`}>
      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        className="w-full px-4 py-3 flex items-center justify-between text-sm font-semibold text-left"
        data-testid={`group-fixtures-toggle-${group}`}>
        <span>
          {group === 'Other' ? 'Other matches' : `Group ${group}`}
          <span className="ml-2 text-xs font-normal text-slate-500"
                data-testid={`group-fixtures-progress-${group}`}>
            {done}/{total}{allDone ? ' · Completed' : ''}
          </span>
        </span>
        {collapsed ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronUp className="h-4 w-4 text-slate-400" />}
      </button>
      {!collapsed && (
        <div className="px-4 pb-3 space-y-3">
          {rounds.map(r => (
            <div key={r}>
              {rounds.length > 1 && (
                <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1"
                     data-testid={`group-fixtures-${group}-round-${r}`}>
                  Round {r}
                </div>
              )}
              <div className="space-y-0">
                {byRound[r].map(m => (
                  <PublicMatchRow key={m.id} match={m} teamById={teamById} playerById={playerById} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const RoundCard = ({ round, matches, teamById, playerById = {}, tnm, isCurrent, collapsedDefault }) => {
  const [collapsed, setCollapsed] = useState(!!collapsedDefault);
  const allDone = matches.every(m => m.status === 'completed' || m.status === 'bye');
  return (
    <div className={`bg-white rounded-2xl border ${isCurrent ? 'border-blue-300 ring-2 ring-blue-100' : ''}`}>
      <button onClick={() => setCollapsed(!collapsed)}
        className="w-full px-4 py-3 flex items-center justify-between text-sm font-semibold text-left"
        data-testid={`round-toggle-${round}`}>
        <span>{getRoundName(tnm, round, matches[0])} {isCurrent && <span className="text-xs text-blue-700 ml-2">(Live)</span>}
          {!isCurrent && allDone && <span className="text-xs text-slate-400 ml-2">Completed</span>}</span>
        {collapsed ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronUp className="h-4 w-4 text-slate-400" />}
      </button>
      {!collapsed && (
        <div className="px-4 pb-3 space-y-2">
          {matches.map(m => <PublicMatchRow key={m.id} match={m} teamById={teamById} playerById={playerById} />)}
        </div>
      )}
    </div>
  );
};

const PublicMatchRow = ({ match, teamById, playerById = {} }) => {
  const t1 = teamById[match.team1_id];
  const t2 = teamById[match.team2_id];
  const t1Name = resolveMatchSide({ playerIds: match.team1_player_ids, team: t1, teamId: match.team1_id }, playerById, teamById) || 'TBD';
  const t2Name = resolveMatchSide({ playerIds: match.team2_player_ids, team: t2, teamId: match.team2_id }, playerById, teamById) || 'TBD';
  const completed = match.status === 'completed' || match.status === 'bye';
  const isWalkover = completed && match.score_team1 == null && match.score_team2 == null;
  return (
    <div className="flex items-start gap-2 py-2.5 border-b last:border-0 text-sm"
         data-testid={`public-match-row-${match.id}`}>
      <div className="text-[10px] text-slate-500 font-mono shrink-0 w-9 pt-1"
           data-testid={`public-match-court-${match.id}`}
           title={`Court ${match.court || 1}`}>Ct {match.court || 1}</div>
      <div className={`flex-1 min-w-0 break-words leading-snug ${completed && match.winner_id === match.team1_id ? 'font-bold text-emerald-700' : 'text-slate-800'}`}
           data-testid={`public-match-t1-${match.id}`}>{t1Name}</div>
      <div className="font-mono font-semibold text-slate-900 shrink-0 w-20 text-center text-xs pt-0.5">
        {isWalkover ? (
          <span
            className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200"
            data-testid={`public-walkover-badge-${match.id}`}
          >
            WO
          </span>
        ) : (
          <>
            {match.score_team1 ?? '–'}<span className="mx-0.5 text-slate-400">:</span>{match.score_team2 ?? '–'}
            {match.tiebreaker_used && match.tiebreaker_score_team1 != null && (
              <span className="block text-[10px] text-slate-500 font-normal">
                (TB: {match.tiebreaker_score_team1}-{match.tiebreaker_score_team2})
              </span>
            )}
          </>
        )}
      </div>
      <div className={`flex-1 min-w-0 break-words leading-snug text-right ${completed && match.winner_id === match.team2_id ? 'font-bold text-emerald-700' : 'text-slate-800'}`}
           data-testid={`public-match-t2-${match.id}`}>{t2Name}</div>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────
// Group + Knockout
// ─────────────────────────────────────────────────────────────
const GroupKnockoutView = ({ tnm, teamById }) => {
  const groupPhaseRounds = tnm.group_phase_rounds || tnm.total_rounds || 0;
  const groupStandings = (tnm.standings || []).filter(s => s.group);
  const byGroup = useMemo(() => {
    const out = {};
    for (const s of groupStandings) (out[s.group] = out[s.group] || []).push(s);
    return out;
  }, [groupStandings]);
  const groupMatches = (tnm.matches || []).filter(m => (m.round || 0) <= groupPhaseRounds);
  const knockoutMatches = (tnm.matches || []).filter(m => (m.round || 0) > groupPhaseRounds);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {Object.entries(byGroup).map(([g, rows]) => (
          <StandingsTable key={g} tnm={tnm} standings={rows} teamById={teamById}
            group={g} testid={`standings-group-${g}`} />
        ))}
      </div>
      {groupMatches.length > 0 && (
        <div>
          <div className="text-sm font-semibold text-slate-700 mb-2 px-1">Group Stage — Fixtures</div>
          <MatchesByGroup tnm={tnm} matches={groupMatches} teamById={teamById} />
        </div>
      )}
      {knockoutMatches.length > 0 && (
        <div>
          <div className="text-sm font-semibold text-slate-700 mb-2 px-1">Knockout</div>
          <BracketView tnm={{ ...tnm, matches: knockoutMatches }} teamById={teamById} />
        </div>
      )}
      <QualifiersSnapshot tnm={tnm} teamById={teamById} />
    </div>
  );
};


// ─────────────────────────────────────────────────────────────
// Who Qualified & Why — read-only frozen snapshot (Phase 3)
// Uses tnm.qualifiers_snapshot + tnm.runners_up_ranking taken at KO-build
// time so the public view can never contradict the real bracket.
// ─────────────────────────────────────────────────────────────
const QUALIFIED_AS_LABEL = {
  group_winner: 'Group Winner',
  group_position: 'Group Position',
  runner_up: 'Runner-up',
  best_runner_up: 'Best Runner-up',
  wildcard: 'Wildcard',
};

const QualifiersSnapshot = ({ tnm, teamById }) => {
  const snapshot = tnm.qualifiers_snapshot || [];
  const runners = tnm.runners_up_ranking || [];
  if (snapshot.length === 0) return null;
  const nameOf = (id, fallback) => teamById[id]?.team_name || fallback || id;
  const labels = forAgainstLabels(tnm.sport);
  return (
    <div className="bg-white rounded-2xl border p-4" data-testid="tournament-qualifiers-snapshot">
      <div className="flex items-center gap-2 text-sm font-bold text-slate-800 mb-1">
        <Trophy className="h-4 w-4 text-emerald-600" /> Who Qualified &amp; Why
      </div>
      <div className="text-xs text-slate-500 mb-3">Confirmed qualifiers that seeded the knockout bracket.</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-slate-500 border-b">
            <tr>
              <th className="text-left py-2 w-10">Seed</th>
              <th className="text-left">Team</th>
              <th className="text-left px-2">Group</th>
              <th className="text-left px-2 hidden sm:table-cell">Pos</th>
              <th className="text-left px-2">Qualified as</th>
            </tr>
          </thead>
          <tbody>
            {snapshot.map((q, i) => (
              <tr key={q.team_id || i} className="border-b last:border-0" data-testid={`qualifier-row-${i}`}>
                <td className="py-2 font-mono text-slate-500">{q.seed ?? i + 1}</td>
                <td className="font-medium text-slate-900">{nameOf(q.team_id, q.team_name)}</td>
                <td className="px-2 text-slate-600">{q.group || '—'}</td>
                <td className="px-2 text-slate-600 hidden sm:table-cell">{q.group_position ?? '—'}</td>
                <td className="px-2">
                  <span className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
                    {QUALIFIED_AS_LABEL[q.qualified_as] || (q.qualified_as || '').replace(/_/g, ' ') || 'Qualified'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {runners.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-bold text-slate-700 mb-1">Best Runners-up Ranking</div>
          <div className="text-[11px] text-slate-400 mb-2">
            Tie-break order: Points → {labels.diff === 'PD' ? 'Points Diff → Points For → Points Against' : 'Goal Difference → Goals For → Goals Against'} → Wins.
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 border-b">
                <tr>
                  <th className="text-left py-2 w-8">#</th>
                  <th className="text-left">Team</th>
                  <th className="text-left px-2">Grp</th>
                  <th className="text-right px-1">Pts</th>
                  <th className="text-right px-1">{labels.diff}</th>
                  <th className="text-right px-1 hidden sm:table-cell">{labels.for}</th>
                  <th className="text-right px-1 hidden sm:table-cell">{labels.against}</th>
                  <th className="text-right px-1">W</th>
                </tr>
              </thead>
              <tbody>
                {runners.map((r, i) => (
                  <tr key={r.team_id || i} className="border-b last:border-0" data-testid={`runner-up-row-${i}`}>
                    <td className="py-2 font-mono text-slate-500">{i + 1}</td>
                    <td className="font-medium text-slate-900">{nameOf(r.team_id, r.team_name)}</td>
                    <td className="px-2 text-slate-600">{r.group || '—'}</td>
                    <td className="text-right px-1 font-bold">{r.points || 0}</td>
                    <td className="text-right px-1">{r.goal_difference > 0 ? '+' : ''}{r.goal_difference || 0}</td>
                    <td className="text-right px-1 hidden sm:table-cell">{r.goals_for || 0}</td>
                    <td className="text-right px-1 hidden sm:table-cell">{r.goals_against || 0}</td>
                    <td className="text-right px-1">{r.won || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};


// ─────────────────────────────────────────────────────────────
// Americano / Mexicano / Mixed
// ─────────────────────────────────────────────────────────────
const AmericanoView = ({ tnm, teamById }) => {
  const cur = tnm.current_round || 0;
  const currentMatches = (tnm.matches || []).filter(m => m.round === cur);
  const completedRounds = (tnm.matches || []).filter(m => m.round !== cur);
  const playerById = buildPlayerById(tnm.teams || []);
  return (
    <>
      <IndividualLeaderboard tnm={tnm} isKing={false} teamById={teamById} playerById={playerById} testid="leaderboard-individual" />
      {tnm.status === 'active' && currentMatches.length > 0 && (
        <div className="bg-white rounded-2xl border p-4" data-testid="current-round-pairings">
          <div className="text-sm font-semibold text-slate-700 mb-3">Round {cur} pairings · Target {tnm.points_per_match || 24} points</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 border-b">
                <tr><th className="text-left py-2">Court</th><th className="text-left">Team A</th><th></th><th className="text-right">Team B</th></tr>
              </thead>
              <tbody>
                {currentMatches.map(m => (
                  <tr key={m.id} className="border-b last:border-0">
                    <td className="py-2 font-mono text-slate-500">{m.court || 1}</td>
                    <td className="font-medium">{resolveSideNames(m.team1_player_ids, playerById, teamById) || '—'}</td>
                    <td className="text-center text-slate-400">vs</td>
                    <td className="text-right font-medium">{resolveSideNames(m.team2_player_ids, playerById, teamById) || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <MatchesByRound tnm={tnm} matches={completedRounds} teamById={teamById} playerById={playerById} collapseCompleted />
    </>
  );
};


// ─────────────────────────────────────────────────────────────
// King of the Court
// ─────────────────────────────────────────────────────────────
const KingView = ({ tnm, teamById }) => {
  const cur = tnm.current_round || 0;
  const rotations = tnm.rotations || [];
  const currentRot = rotations.find(r => r.round === cur);
  const assignmentsObj = currentRot?.assignments || {};
  const courts = Object.keys(assignmentsObj).map(Number).sort((a, b) => a - b);
  const waiting = currentRot?.waiting || [];
  const playerById = buildPlayerById(tnm.teams || []);
  return (
    <>
      {tnm.status === 'active' && currentRot && (
        <div className="bg-white rounded-2xl border p-4" data-testid="king-current-courts">
          <div className="text-sm font-semibold text-slate-700 mb-3">Round {cur} · Court assignments</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {courts.map(c => (
              <div key={c} className={`rounded-xl border-2 p-3 ${c === 1 ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-slate-50'}`}
                data-testid={`king-court-${c}`}>
                <div className="flex items-center gap-2 text-sm font-bold mb-1">
                  {c === 1 ? <><Trophy className="h-4 w-4 text-amber-600" /> Court 1 (King)</> : `Court ${c}`}
                </div>
                <div className="text-sm">
                  {(assignmentsObj[c] || []).map(p => (
                    <div key={p}>{resolveEntityName(p, playerById, teamById)}</div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {waiting.length > 0 && (
            <div className="mt-3 p-3 bg-slate-100 rounded-xl" data-testid="king-waiting">
              <div className="text-xs uppercase font-semibold text-slate-500 mb-1">Waiting</div>
              <div className="text-sm flex flex-wrap gap-x-3">
                {waiting.map(p => <span key={p}>{resolveEntityName(p, playerById, teamById)}</span>)}
              </div>
            </div>
          )}
        </div>
      )}
      <IndividualLeaderboard tnm={tnm} isKing teamById={teamById} playerById={playerById} testid="leaderboard-king" />
      <MatchesByRound tnm={tnm} matches={tnm.matches || []} teamById={teamById} playerById={playerById} collapseCompleted />
    </>
  );
};


// Individual standings: # | Player | W | T | L | P | +/- (King also shows Rds C1, its primary sort)
const IndividualLeaderboard = ({ tnm, isKing, teamById, playerById, testid }) => {
  const ranked = useMemo(() => {
    const cmp = (a, b) => {
      if (isKing && (b.rounds_on_court_1 || 0) !== (a.rounds_on_court_1 || 0)) {
        return (b.rounds_on_court_1 || 0) - (a.rounds_on_court_1 || 0);
      }
      return (b.cumulative_score || 0) - (a.cumulative_score || 0)
        || (b.plus_minus || 0) - (a.plus_minus || 0)
        || (b.won || 0) - (a.won || 0);
    };
    return [...(tnm.standings || [])].sort(cmp);
  }, [tnm.standings, isKing]);
  const fmtPM = (v) => `${(v || 0) > 0 ? '+' : ''}${v || 0}`;
  return (
    <div className="bg-white rounded-2xl border p-4" data-testid={testid}>
      <div className="text-sm font-semibold text-slate-700 mb-2">Leaderboard{isKing ? ' · Rounds on Court 1' : ''}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-slate-500 border-b">
            <tr>
              <th className="text-left py-2">#</th>
              <th className="text-left">Player</th>
              {isKing && <th className="text-right px-1">Rds&nbsp;C1</th>}
              <th className="text-right px-1">W</th>
              <th className="text-right px-1 hidden sm:table-cell">T</th>
              <th className="text-right px-1">L</th>
              <th className="text-right px-1 font-bold">P</th>
              <th className="text-right px-1">+/-</th>
            </tr>
          </thead>
          <tbody>
            {ranked.map((s, i) => (
              <tr key={s.id} className="border-b last:border-0">
                <td className="py-2 font-mono text-slate-500">{i + 1}</td>
                <td className="font-medium text-slate-900" data-testid={`public-leaderboard-name-${i}`}>{resolveEntityName(s.team_id, playerById, teamById)}</td>
                {isKing && <td className="text-right px-1 font-semibold">{s.rounds_on_court_1 || 0}</td>}
                <td className="text-right px-1">{s.won || 0}</td>
                <td className="text-right px-1 hidden sm:table-cell">{s.drawn || 0}</td>
                <td className="text-right px-1">{s.lost || 0}</td>
                <td className="text-right px-1 font-bold">{s.cumulative_score || 0}</td>
                <td className={`text-right px-1 ${(s.plus_minus || 0) < 0 ? 'text-red-600' : 'text-slate-700'}`}>{fmtPM(s.plus_minus)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


const PublicTopScorers = ({ tnm }) => {
  const rows = (tnm.top_scorers || []).filter(r => (r.total_goals || 0) > 0);
  if (rows.length === 0) return null;
  return (
    <div className="bg-white rounded-2xl border p-4" data-testid="public-top-scorers">
      <div className="text-sm font-bold text-slate-800 mb-3">⚽ Top Scorers</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-slate-500 border-b">
              <th className="py-2 pr-2 w-8">#</th>
              <th className="py-2 pr-2">Player</th>
              <th className="py-2 pr-2">Team</th>
              <th className="py-2 pl-2 text-center">Goals</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.player_id || r.player_name}-${r.team_id}`} className="border-b last:border-0" data-testid={`public-top-scorer-${i}`}>
                <td className="py-2 pr-2 text-slate-400 font-mono">{i + 1}</td>
                <td className="py-2 pr-2 font-medium text-slate-900">{r.player_name}</td>
                <td className="py-2 pr-2 text-slate-600">{r.team_name}</td>
                <td className="py-2 pl-2 text-center font-bold text-emerald-700">{r.total_goals}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


const FooterRefresh = () => (
  <div className="fixed bottom-3 left-1/2 -translate-x-1/2 bg-white border shadow-sm rounded-full px-3 py-1.5 text-[11px] text-slate-500 flex items-center gap-1.5 z-30">
    <span className="h-2 w-2 bg-emerald-500 rounded-full animate-pulse" /> Live · refreshes every 30 seconds
  </div>
);
